/**
 * EngineStatsTracker
 *
 * Generic performance stats tracker for engine classes.
 * Handles total calls counting, running average time, and custom counters.
 *
 * DebugTraceManager
 *
 * Generic debug trace toggle for engine-level DEBUG_TRACES objects.
 */

/**
 * Generic performance stats tracker.
 *
 * T is the shape of the stats object. Must include:
 *   - a "total calls" numeric field (specified by totalCallsKey)
 *   - an "average time" numeric field (specified by averageTimeKey)
 *
 * Usage:
 *   const tracker = new EngineStatsTracker(
 *     { totalCalculations: 0, averageTime: 0, lastCalculationTime: 0 },
 *     'totalCalculations',
 *     'averageTime'
 *   );
 *   tracker.update(duration, (stats) => { stats.lastCalculationTime = duration; });
 *   tracker.get();  // returns a shallow copy
 *   tracker.reset(); // resets to initial values
 */
export class EngineStatsTracker<T extends Record<string, number>> {
    private stats: T;
    private readonly initialStats: T;
    private readonly totalCallsKey: keyof T;
    private readonly averageTimeKey: keyof T;

    constructor(
        initialStats: T,
        totalCallsKey: keyof T,
        averageTimeKey: keyof T
    ) {
        this.initialStats = { ...initialStats };
        this.stats = { ...initialStats };
        this.totalCallsKey = totalCallsKey;
        this.averageTimeKey = averageTimeKey;
    }

    /**
     * Record a new call with its duration and optional custom updates.
     * Increments total calls and recalculates running average automatically.
     */
    update(duration: number, customUpdates?: (stats: T) => void): void {
        (this.stats[this.totalCallsKey] as number)++;
        const totalCalls = this.stats[this.totalCallsKey] as number;
        const prevAvg = this.stats[this.averageTimeKey] as number;
        (this.stats[this.averageTimeKey] as number) =
            (prevAvg * (totalCalls - 1) + duration) / totalCalls;

        if (customUpdates) {
            customUpdates(this.stats);
        }
    }

    /**
     * Return a shallow copy of current stats.
     */
    get(): T {
        return { ...this.stats };
    }

    /**
     * Reset stats to initial values.
     */
    reset(): void {
        this.stats = { ...this.initialStats };
    }
}

/**
 * Generic debug trace manager for engine-level DEBUG_TRACES objects.
 *
 * Usage:
 *   const DEBUG_TRACES = DebugTraceManager.create({
 *     CALCULATION_ENGINE: false,
 *     COORDINATE_CALC: false,
 *   });
 *
 *   // Toggle all traces
 *   DebugTraceManager.setAll(DEBUG_TRACES, true);
 *
 *   // Read a trace flag
 *   if (DEBUG_TRACES.CALCULATION_ENGINE) { ... }
 */
export class DebugTraceManager {
    /**
     * Create a debug traces object (convenience identity function for type safety).
     */
    static create<T extends Record<string, boolean>>(traces: T): T {
        return traces;
    }

    /**
     * Set all trace flags to the given value.
     */
    static setAll<T extends Record<string, boolean>>(traces: T, enabled: boolean): void {
        for (const key of Object.keys(traces)) {
            (traces as Record<string, boolean>)[key] = enabled;
        }
    }
}
